import { useParams, Link, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import {
    SiMeta,
    SiGoogle,
    SiTiktok,
    SiSnapchat,
} from "react-icons/si";
import {
    ArrowLeft,
    ArrowRight,
    Check,
    ShieldCheck,
    Zap,
    Wallet,
    Headphones,
    BadgeCheck,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TrustBar from "../components/sections/TrustBar";
import StatsSection from "../components/sections/StatsSection";
import FinalCTA from "../components/sections/FinalCTA";
import TrustpilotWidget from "../components/TrustpilotWidget";
import FloatingContact from "../components/FloatingContact";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../contexts/LanguageContext";
import { localizedPath } from "../lib/locale";
import { PLATFORM_TR, PLATFORM_PAGE_TR } from "../data/platformDetailTr";
import { PLATFORM_ES, PLATFORM_PT, PLATFORM_RU, PLATFORM_PAGE_ES, PLATFORM_PAGE_PT, PLATFORM_PAGE_RU } from "../data/platformDetailI18n";

const SITE_URL = "https://adrise-agency.com";

const PLATFORM_PAGE_EN = {
    allPlatforms: "All platforms",
    badge: "Platinum HIVA Partner · Credit Line",
    apply: (n) => `Get Free ${n} Access`,
    seePricing: "See pricing",
    noSub: "No subscription · Credit Line",
    perMonth: "/month",
    noFee: (n) => `No monthly fee for Credit Line ad accounts on ${n}. You only pay a volumetric top-up fee from 4%.`,
    applyNow: "Create Free Account",
    allIncluded: "All 7 platforms included ·",
    feeLadder: "see the fee ladder",
    white: "White-hat: 4% → 2% as volume grows",
    gray: "Gray-hat: 7% → 3% as volume grows",
    casino: "Casino & Gambling: 10% → 7%",
    cashback: "Cashback up to 2% above $1M",
    whatYouGet: "What you get",
    builtFor: "Built for advertisers who refuse to share scaling ceiling.",
    builtText: (n) => `Every ${n} account on Adrise ships from our Platinum HIVA Partner infrastructure — funded by us, topped up by you in crypto. You get the trust tier reserved for eight-figure agencies, with none of the gatekeeping.`,
    verticalsEyebrow: "Verticals welcomed",
    verticalsTitle: (n) => `What you can run on ${n}.`,
    verticalsText1: "Volumetric fees apply across every vertical: ",
    verticalsWhite: "white-hat 4% → 2%",
    verticalsGray: "gray-hat 7% → 3%",
    verticalsText2: " as your spend scales past $1M. No monthly subscription.",
    verticalsCasino: " Casino & Gambling is unlocked on every platform at ",
    verticalsCasino2: " with dedicated compliance review.",
    why: [
        { title: "Live in < 5 min", text: "Account provisioning is instant once your wallet is funded." },
        { title: "Crypto top-up", text: "USDT (TRC-20 / ERC-20), USDC (ERC-20). Confirms in 1–3 minutes." },
        { title: "Whitelisted", text: "Platinum HIVA trust tier means fewer false-positive bans." },
        { title: "24/7 WhatsApp & Telegram", text: "A real human, on direct chat, in 3 minutes." },
    ],
    faqEyebrow: (n) => `${n} FAQ`,
    faqTitle: (n) => `Common questions about ${n}.`,
    otherEyebrow: "Other platforms",
    otherTitle: (n) => `Pair ${n} with another channel.`,
    seeLadder: "See the fee ladder →",
};

const BingMark = () => (
    <span className="font-display font-black text-3xl tracking-tight">b·</span>
);
const TaboolaMark = () => (
    <span className="font-display font-black text-xl tracking-[0.18em]">
        TABOOLA
    </span>
);
const OutbrainMark = () => (
    <span className="font-display font-black text-xl tracking-[0.18em]">
        OUTBRAIN
    </span>
);

const PLATFORMS = {
    meta: {
        name: "Meta",
        Icon: SiMeta,
        accent: "#1877F2",
        family: "Facebook · Instagram · Messenger · WhatsApp",
        tagline: "The most popular paid traffic engine on the planet.",
        h1: "Meta Agency Ad Accounts",
        intro:
            "Plug into the world's largest auction with a Platinum HIVA Partner Business Manager — the same trust tier reserved for $20M+/mo agencies. Adrise Credit Line means we fund your account directly with the platform; you top us up in crypto and scale aggressively without spend caps and without the ban roulette.",
        ad_features: [
            "Whitelisted Platinum HIVA Business Manager",
            "Run Facebook, Instagram, Messenger & WhatsApp inventory",
            "Advantage+ Shopping, Reach, Awareness, Lead Gen — all formats",
            "All major verticals approved (gray-hat-friendly)",
            "Casino & Gambling unlocked (10% → 7% fee)",
            "Direct Meta human reps for escalations",
        ],
        approved_verticals: [
            "E-commerce / DTC",
            "Aggressive D2C / Before-After",
            "Finance & Insurance",
            "Crypto & Web3",
            "Nutra & Health",
            "Dating & Apps",
            "Lead Gen / Local",
            "Gray-hat (compliance review)",
            "Casino & Gambling (premium fee)",
        ],
        faqs: [
            {
                q: "What's a Platinum HIVA Partner Business Manager?",
                a: "Platinum HIVA (High Value Advertiser) is Meta's top-tier internal Business Manager trust classification, reserved for agencies operating at $20M+ monthly spend. It correlates with higher account stability, faster moderation, lower CPMs, and direct human-rep access — not bot replies.",
            },
            {
                q: "Can I run my existing pixels and BMs?",
                a: "Yes. We share an asset assignment to your existing pixel and pages, so your historical data, Custom Audiences, and Lookalikes carry over. Bulletproof BM structure protects them across replacements.",
            },
            {
                q: "What's the difference between standard and Casino fee?",
                a: "White-hat verticals (e-commerce, SaaS, B2B, lead gen) start at a 4% top-up fee and gray-hat verticals (finance, crypto, nutra, dating) at 7% — both drop as your monthly volume grows. Casino & Gambling on Meta requires a dedicated compliance review and runs at a 10% → 7% — still significantly cheaper than building a comparable structure on your own.",
            },
        ],
    },
    google: {
        name: "Google",
        Icon: SiGoogle,
        accent: "#fff",
        family: "Search · YouTube · Display · Shopping · Performance Max",
        tagline: "High-intent demand, captured at the moment of truth.",
        h1: "Google Agency Ad Accounts",
        intro:
            "Tap into Google's vast network with whitelisted MCC accounts that ship with elevated trust scores. Higher approval rates, broader reach, and better conversions across Search, YouTube, Display, Shopping & Performance Max — without the daily-spend handcuffs new accounts get hit with.",
        ad_features: [
            "Whitelisted Google Ads MCC accounts",
            "Search, YouTube, Display, Shopping & Performance Max",
            "Higher trust score → fewer false-positive suspensions",
            "Faster ad approvals, lower CPCs",
            "Multi-GEO ad accounts (US / EU / UK)",
            "Direct Google rep escalation path",
        ],
        approved_verticals: [
            "E-commerce / Shopping",
            "B2B Lead Gen",
            "Local Services",
            "Finance & Insurance",
            "Health & Nutra (compliance review)",
            "Crypto & Web3 (whitelist required)",
            "Dating & Apps",
        ],
        faqs: [
            {
                q: "Are these MCC sub-accounts or standalone accounts?",
                a: "Standalone whitelisted Google Ads accounts under our agency MCC. You get full editor access; we handle the trust-tier infrastructure underneath.",
            },
            {
                q: "Will my Performance Max campaigns work?",
                a: "Yes. Full PMax support, including audience signals, asset groups, and conversion tracking via your own GA4 / pixel.",
            },
            {
                q: "How fast are ad approvals?",
                a: "On whitelisted Adrise inventory, typical ad approval is under 5 minutes vs. 5–12 hours on a standard account.",
            },
        ],
    },
    tiktok: {
        name: "TikTok",
        Icon: SiTiktok,
        accent: "#fff",
        family: "Spark Ads · In-Feed · TopView · Pulse",
        tagline: "The fastest-growing paid channel — without the rookie cap.",
        h1: "TikTok Agency Ad Accounts",
        intro:
            "Engage the fastest-growing paid audience with TikTok's high-impact ad formats through our agency Business Centers. Higher daily limits, faster approvals, and broader vertical eligibility than self-serve TikTok For Business will ever give you.",
        ad_features: [
            "Whitelisted TikTok Business Center seats",
            "Spark Ads, In-Feed, TopView, Pulse, Lead Gen",
            "No daily-spend caps · unlimited scale",
            "Faster creative review (under 1 hour)",
            "Multi-GEO sourcing (US / EU / SEA)",
            "Direct TikTok partner escalation",
        ],
        approved_verticals: [
            "E-commerce / DTC",
            "Apps & Gaming",
            "Finance (compliance review)",
            "Health & Wellness",
            "Lead Gen",
            "Brand & Awareness campaigns",
        ],
        faqs: [
            {
                q: "Can I run Spark Ads with creator authorization?",
                a: "Yes. Full Spark Ads support — request creator code, boost organic posts, and run paid Spark campaigns under your branded handle.",
            },
            {
                q: "Are aggressive D2C creatives allowed?",
                a: "TikTok's policy is stricter than Meta's, but our Business Center seats give you access to a wider creative window. Borderline creatives go through a 30-min compliance pre-check.",
            },
            {
                q: "Do you support TikTok Shop ads?",
                a: "Yes for approved TikTok Shop sellers. We can attach your shop SKU catalog and run Shop Ads, GMV Max, and product video ads.",
            },
        ],
    },
    snapchat: {
        name: "Snapchat",
        Icon: SiSnapchat,
        accent: "#FFFC00",
        family: "Snap Ads · AR Lenses · Story Ads · Spotlight",
        tagline: "Gen Z attention — at scale, without spend caps.",
        h1: "Snapchat Agency Ad Accounts",
        intro:
            "Snapchat is the cheapest CPM in tier-1 markets right now and the team that gets it built infrastructure for it. Adrise agency Snap accounts ship at higher trust tiers — meaning aggressive scaling without ramp-up periods and access to AR Lens campaigns that take weeks to be approved on standard accounts.",
        ad_features: [
            "Whitelisted Snap Business Manager seats",
            "Snap Ads, AR Lenses, Story Ads, Spotlight Ads",
            "No daily-spend caps · unlimited scale",
            "Faster creative & lens approvals",
            "Multi-GEO targeting (US / EU / MENA)",
            "Direct Snap partner escalation",
        ],
        approved_verticals: [
            "E-commerce / DTC",
            "Apps & Gaming",
            "Beauty & Fashion",
            "Health & Nutra (compliance review)",
            "Lead Gen",
        ],
        faqs: [
            {
                q: "Are AR Lens campaigns supported?",
                a: "Yes. Full AR Lens campaign support including custom Lens uploads, Lens Web Builder integration, and Story Ads with Lens swipe-up.",
            },
            {
                q: "How does Snap compare on CPM?",
                a: "Across DTC and apps verticals, Snap typically runs 30–50% cheaper CPM than Meta in tier-1 GEOs — making it the highest ROAS channel for many of our clients.",
            },
            {
                q: "Can I run shopping catalog ads?",
                a: "Yes — Dynamic Product Ads with Snap Shopping catalogs are fully supported.",
            },
        ],
    },
    bing: {
        name: "Bing / Microsoft",
        Icon: BingMark,
        accent: "#fff",
        family: "Search · Audience Network · Outlook · MSN",
        tagline: "The cheapest high-intent search inventory in tier-1.",
        h1: "Microsoft (Bing) Agency Ad Accounts",
        intro:
            "Microsoft Advertising is the underrated tier-1 channel. Same Google-like search intent, half the CPC, and a desktop-skewed audience that converts. Adrise agency Microsoft accounts ship with whitelisted approval pathways and direct rep access — so your aggressive verticals get the green light.",
        ad_features: [
            "Whitelisted Microsoft Advertising agency accounts",
            "Search, Audience Network, Outlook, MSN inventory",
            "Higher daily-spend limits · unlimited scale",
            "Faster ad approval cycles",
            "Multi-GEO sourcing (US / EU / UK)",
            "Direct Microsoft Advertising rep access",
        ],
        approved_verticals: [
            "E-commerce / Shopping",
            "B2B Lead Gen",
            "Finance & Insurance",
            "Health & Pharma (compliance review)",
            "Local Services",
        ],
        faqs: [
            {
                q: "Why bother with Microsoft if I already run Google?",
                a: "Microsoft's audience is older, more affluent, and desktop-heavy — meaning higher AOV in e-commerce, finance, and B2B. CPCs are typically 30–50% lower than Google for the same keyword set.",
            },
            {
                q: "Does my Google import work?",
                a: "Yes. We support full Google Ads → Microsoft Advertising imports and ongoing sync.",
            },
            {
                q: "Are there minimum spend requirements?",
                a: "No daily minimums on Adrise inventory. Test with $50/day or scale to $50k/day — same trust tier.",
            },
        ],
    },
    taboola: {
        name: "Taboola",
        Icon: TaboolaMark,
        accent: "#fff",
        family: "Native discovery on premium publishers",
        tagline: "Native ads at scale — across the world's biggest publishers.",
        h1: "Taboola Agency Ad Accounts",
        intro:
            "Taboola is the largest discovery network on premium publisher sites — CNN, USA Today, NBC, Bloomberg and thousands more. Adrise agency Taboola accounts ship with elevated trust, faster moderation, and direct rep access for high-volume native campaigns.",
        ad_features: [
            "Whitelisted Taboola agency seats",
            "Native discovery on premium publishers",
            "No daily-spend caps · unlimited scale",
            "Faster creative + landing page approvals",
            "Direct Taboola rep escalation",
            "Multi-GEO sourcing (US / EU / APAC)",
        ],
        approved_verticals: [
            "E-commerce / DTC",
            "Finance & Insurance",
            "Health & Nutra (compliance review)",
            "Crypto & Web3 (whitelist required)",
            "Lead Gen",
            "Long-form content / Advertorial",
        ],
        faqs: [
            {
                q: "What's the minimum daily budget?",
                a: "We recommend $200/day minimum to gather meaningful learning data, but there's no hard minimum on the platform.",
            },
            {
                q: "Are advertorial / native landing pages allowed?",
                a: "Yes — Taboola is the channel built for advertorials. We pre-review your LPs to keep them within compliance.",
            },
            {
                q: "Can I run health & finance creatives?",
                a: "Yes, both with compliance review. Taboola's native format is one of the few channels still wide open for these verticals at scale.",
            },
        ],
    },
    outbrain: {
        name: "Outbrain",
        Icon: OutbrainMark,
        accent: "#fff",
        family: "Native discovery on top news placements",
        tagline: "Premium native — distributed across the open web.",
        h1: "Outbrain Agency Ad Accounts",
        intro:
            "Outbrain pioneered native discovery and still owns the most premium publisher placements globally. Adrise agency Outbrain seats ship with higher trust tiers — meaning faster approvals, broader vertical coverage, and direct rep access.",
        ad_features: [
            "Whitelisted Outbrain agency seats",
            "Native placements on premium news sites",
            "No daily-spend caps · unlimited scale",
            "Faster creative & advertorial approvals",
            "Direct Outbrain rep escalation",
            "Multi-GEO sourcing (US / EU / APAC)",
        ],
        approved_verticals: [
            "E-commerce / DTC",
            "Finance & Insurance",
            "Health & Nutra (compliance review)",
            "Crypto & Web3 (whitelist required)",
            "Lead Gen",
            "Long-form content / Advertorial",
        ],
        faqs: [
            {
                q: "Outbrain vs Taboola — which is better?",
                a: "Both are excellent, with overlapping but distinct publisher inventory. Most clients run them side-by-side and let the data decide. With Adrise there is no subscription — both are included, you only pay the top-up fee.",
            },
            {
                q: "What's the typical CPC range?",
                a: "Native CPCs run $0.20–$1.50 depending on GEO, vertical, and placement. ROAS depends entirely on creative + LP — and we share top-performer playbooks at onboarding.",
            },
            {
                q: "Can I scale to $50k/day?",
                a: "Yes. Native scales — the inventory is there. Your only constraint is creative iteration speed.",
            },
        ],
    },
};

const platformList = Object.entries(PLATFORMS).map(([slug, p]) => ({
    slug,
    name: p.name,
}));

const PLATFORM_CONTENT = { tr: PLATFORM_TR, es: PLATFORM_ES, pt: PLATFORM_PT, ru: PLATFORM_RU };
const PLATFORM_PAGE_COPY = { tr: PLATFORM_PAGE_TR, es: PLATFORM_PAGE_ES, pt: PLATFORM_PAGE_PT, ru: PLATFORM_PAGE_RU };

export default function PlatformDetailPage() {
    const { slug } = useParams();
    const { language } = useLanguage();
    const base = PLATFORMS[slug];
    const overlay = PLATFORM_CONTENT[language]?.[slug];
    const platform = base ? (overlay ? { ...base, ...overlay } : base) : null;
    const L = PLATFORM_PAGE_COPY[language] || PLATFORM_PAGE_EN;
    const lp = (path) => localizedPath(language, path);

    if (!platform) {
        return <Navigate to="/" replace />;
    }

    const { Icon, accent } = platform;
    const WHY_ICONS = [Zap, Wallet, ShieldCheck, Headphones];

    const productJsonLd = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: `${platform.name} Agency Ad Account`,
        description: platform.intro,
        brand: { "@type": "Brand", name: "Adrise" },
        offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
            url: `${SITE_URL}/platforms/${slug}`,
            availability: "https://schema.org/InStock",
            priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: "0",
                priceCurrency: "USD",
                unitText: "MONTH",
                description: "No monthly subscription. Volumetric top-up fee from 4% down to 2%.",
            },
        },
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "320", bestRating: "5" },
    };

    const breadcrumbJsonLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: "Platforms", item: `${SITE_URL}/#platforms` },
            { "@type": "ListItem", position: 3, name: platform.name, item: `${SITE_URL}/platforms/${slug}` },
        ],
    };

    const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: platform.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    };

    return (
        <main
            data-testid={`platform-detail-${slug}`}
            className="bg-background text-foreground min-h-screen"
        >
            <SEOHead
                title={`${platform.h1} — Platinum HIVA Partner Credit Line | Adrise`}
                description={platform.intro.substring(0, 160)}
                canonical={`${SITE_URL}/platforms/${slug}`}
                keywords={[
                    `${platform.name} agency account`,
                    `${platform.name} credit line`,
                    "platinum hiva",
                    "whitelisted ad account",
                    `${platform.name} ad account`,
                ]}
                jsonLd={[productJsonLd, faqJsonLd, breadcrumbJsonLd]}
            />

            <Navbar />

            {/* Hero */}
            <section className="relative pt-32 md:pt-40 pb-16 md:pb-20 overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
                <div className="absolute inset-0 noise pointer-events-none" />

                <div className="container-skala relative">
                    <Link
                        to={lp("/") + "#platforms"}
                        data-testid="platform-back-link"
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
                    >
                        <ArrowLeft className="w-4 h-4" /> {L.allPlatforms}
                    </Link>

                    <div className="grid lg:grid-cols-12 gap-10 items-start">
                        <div className="lg:col-span-8 space-y-6">
                            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5">
                                <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                                <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-primary">
                                    {L.badge}
                                </span>
                            </div>

                            <div className="flex items-center gap-5">
                                <div
                                    className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-card border border-white/10 grid place-items-center"
                                    style={{ color: accent }}
                                >
                                    <Icon className="w-8 h-8 md:w-10 md:h-10" />
                                </div>
                                <div>
                                    <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight">
                                        {platform.h1}
                                    </h1>
                                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mt-2">
                                        {platform.family}
                                    </p>
                                </div>
                            </div>

                            <p className="text-lg md:text-xl font-display text-foreground/90">
                                {platform.tagline}
                            </p>
                            <p className="text-base text-muted-foreground leading-relaxed max-w-2xl">
                                {platform.intro}
                            </p>

                            <div className="flex flex-wrap gap-3 pt-2">
                                <Link to="/signup" data-testid={`platform-cta-apply-${slug}`}>
                                    <Button
                                        size="lg"
                                        className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-12 px-7 shadow-[0_0_30px_rgba(0, 0, 0,0.45)]"
                                    >
                                        {L.apply(platform.name)}
                                        <ArrowRight className="ml-2 w-4 h-4" />
                                    </Button>
                                </Link>
                                <Link to={lp("/") + "#pricing"} data-testid={`platform-cta-pricing-${slug}`}>
                                    <Button
                                        size="lg"
                                        variant="outline"
                                        className="border-white/15 bg-white/[0.02] hover:bg-white/[0.06] h-12 px-7"
                                    >
                                        {L.seePricing}
                                    </Button>
                                </Link>
                            </div>
                        </div>

                        {/* Pricing Card */}
                        <div className="lg:col-span-4">
                            <div className="bg-card border border-primary/40 rounded-2xl p-7 glow-primary relative">
                                <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-primary mb-2">
                                    {L.noSub}
                                </p>
                                <div className="flex items-baseline gap-1.5 mb-4">
                                    <span className="font-display font-black text-5xl">
                                        $0
                                    </span>
                                    <span className="text-muted-foreground">{L.perMonth}</span>
                                </div>
                                <p className="text-sm text-muted-foreground mb-5">{L.noFee(platform.name)}</p>
                                <Link to="/signup" data-testid={`platform-card-cta-${slug}`}>
                                    <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11 mb-3">
                                        {L.applyNow}
                                    </Button>
                                </Link>
                                <div className="text-xs text-muted-foreground text-center">
                                    {L.allIncluded}{" "}
                                    <Link
                                        to={lp("/") + "#pricing"}
                                        className="text-foreground hover:text-primary underline-offset-4 hover:underline"
                                    >
                                        {L.feeLadder}
                                    </Link>
                                </div>

                                <div className="mt-5 pt-5 border-t border-white/10 space-y-2 text-xs text-muted-foreground">
                                    <div className="flex items-center gap-2">
                                        <Check className="w-3.5 h-3.5 text-primary" />
                                        <span>{L.white}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Check className="w-3.5 h-3.5 text-primary" />
                                        <span>{L.gray}</span>
                                    </div>
                                    {slug === "meta" && (
                                        <div className="flex items-center gap-2">
                                            <Check className="w-3.5 h-3.5 text-primary" />
                                            <span>{L.casino}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <Check className="w-3.5 h-3.5 text-[#2FA57C]" />
                                        <span className="text-[#2FA57C]">{L.cashback}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What you get */}
            <section className="py-20 md:py-24">
                <div className="container-skala">
                    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                        <div className="space-y-5">
                            <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary">
                                {L.whatYouGet}
                            </p>
                            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight leading-[1.1]">
                                {L.builtFor}
                            </h2>
                            <p className="text-muted-foreground leading-relaxed">{L.builtText(platform.name)}</p>
                        </div>
                        <ul className="space-y-3">
                            {platform.ad_features.map((f) => (
                                <li
                                    key={f}
                                    className="flex items-start gap-3 bg-card border border-white/[0.08] rounded-xl p-4"
                                >
                                    <span className="mt-0.5 grid place-items-center w-6 h-6 rounded-full bg-primary/15 text-primary shrink-0">
                                        <Check className="w-3.5 h-3.5" />
                                    </span>
                                    <span className="text-sm md:text-base text-foreground/90">
                                        {f}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Approved verticals */}
            <section className="py-20 md:py-24 border-y border-white/5 bg-white/[0.01]">
                <div className="container-skala">
                    <div className="max-w-3xl mb-10 space-y-4">
                        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary">
                            {L.verticalsEyebrow}
                        </p>
                        <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
                            {L.verticalsTitle(platform.name)}
                        </h2>
                        <p className="text-muted-foreground">
                            {L.verticalsText1}
                            <span className="font-mono text-foreground">{L.verticalsWhite}</span>
                            ,{" "}
                            <span className="font-mono text-foreground">{L.verticalsGray}</span>
                            {L.verticalsText2}
                            {(
                                <>
                                    {L.verticalsCasino}
                                    <span className="font-mono text-foreground">10% → 7%</span>
                                    {L.verticalsCasino2}
                                </>
                            )}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {platform.approved_verticals.map((v) => (
                            <span
                                key={v}
                                className="inline-flex items-center gap-2 bg-card border border-white/10 rounded-full px-4 py-2 text-sm text-foreground/85 hover:border-primary/40 transition-colors"
                            >
                                <BadgeCheck className="w-3.5 h-3.5 text-primary" />
                                {v}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Adrise */}
            <section className="py-20 md:py-24">
                <div className="container-skala">
                    <div className="grid md:grid-cols-4 gap-4 md:gap-5">
                        {L.why.map((w, i) => ({ ...w, Icon: WHY_ICONS[i] })).map((c) => (
                            <article
                                key={c.title}
                                className="bg-card border border-white/[0.08] rounded-2xl p-6"
                            >
                                <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 grid place-items-center mb-4">
                                    <c.Icon className="w-5 h-5 text-primary" />
                                </div>
                                <h3 className="font-display font-semibold text-lg mb-1.5">
                                    {c.title}
                                </h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    {c.text}
                                </p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <TrustBar />
            <StatsSection />
            <TrustpilotWidget />

            {/* Platform-specific FAQ */}
            <section className="py-20 md:py-28">
                <div className="container-skala">
                    <div className="text-center max-w-3xl mx-auto mb-10 space-y-3">
                        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary">
                            {L.faqEyebrow(platform.name)}
                        </p>
                        <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
                            {L.faqTitle(platform.name)}
                        </h2>
                    </div>
                    <Accordion
                        type="single"
                        collapsible
                        className="max-w-3xl mx-auto"
                        data-testid={`platform-faq-${slug}`}
                    >
                        {platform.faqs.map((f, i) => (
                            <AccordionItem
                                key={i}
                                value={`item-${i}`}
                                className="border-white/[0.08]"
                            >
                                <AccordionTrigger className="text-left font-display font-medium text-base md:text-lg py-6 hover:text-primary hover:no-underline">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-muted-foreground text-sm md:text-base leading-relaxed pb-6">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* Other platforms */}
            <section className="py-20 md:py-24 border-t border-white/5">
                <div className="container-skala">
                    <div className="flex items-end justify-between mb-8">
                        <div>
                            <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary mb-2">
                                {L.otherEyebrow}
                            </p>
                            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
                                {L.otherTitle(platform.name)}
                            </h2>
                        </div>
                        <Link
                            to={lp("/") + "#pricing"}
                            className="text-sm text-foreground/85 hover:text-primary"
                        >
                            {L.seeLadder}
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        {platformList
                            .filter((p) => p.slug !== slug)
                            .map((p) => (
                                <Link
                                    key={p.slug}
                                    to={lp(`/platforms/${p.slug}`)}
                                    data-testid={`platform-cross-link-${p.slug}`}
                                    className="bg-card border border-white/[0.08] hover:border-primary/40 rounded-xl px-4 py-3 text-sm font-medium hover:text-primary transition-colors"
                                >
                                    {p.name}
                                </Link>
                            ))}
                    </div>
                </div>
            </section>

            <FinalCTA />
            <Footer />
            <FloatingContact />
        </main>
    );
}
